import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, CheckCircle, XCircle, Award } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CertificateVerifier = () => {
  const [certificateId, setCertificateId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      // Encode the certificate ID to handle special characters like /
      const encodedId = encodeURIComponent(certificateId);
      const response = await axios.get(`${API}/certificates/verify/${encodedId}`);
      setResult(response.data);
    } catch (err) {
      console.error('Verification error:', err);
      setError('Certificate not found. Please check the certificate ID and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title="Verify Certificate — Genius Academy Kalaburagi"
        description="Verify the authenticity of any Genius Academy certificate online. Enter your certificate ID to instantly view student name, course, grade and issue date."
        path="/verify"
        keywords="verify Genius Academy certificate, computer course certificate verification, Kalaburagi computer institute certificate"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Verify Certificate', path: '/verify' }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-cyan-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="bg-cyan-100 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-cyan-600" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6" data-testid="verify-page-title">
              Verify <span className="text-cyan-600">Certificate</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Enter your certificate ID to verify its authenticity and view details.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Verification Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8"
          >
            <form onSubmit={handleVerify} className="space-y-6">
              <div>
                <label htmlFor="certificateId" className="block text-sm font-medium text-slate-700 mb-2">
                  Certificate ID
                </label>
                <input
                  type="text"
                  id="certificateId"
                  value={certificateId}
                  onChange={(e) => setCertificateId(e.target.value)}
                  required
                  className="bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all w-full text-lg"
                  placeholder="Enter certificate ID (e.g., GA2024001)"
                  data-testid="certificate-id-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-300 text-white rounded-full px-8 py-4 font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 disabled:transform-none flex items-center justify-center gap-2"
                data-testid="verify-button"
              >
                <Search className="w-5 h-5" />
                {loading ? 'Verifying...' : 'Verify Certificate'}
              </button>
            </form>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-8 bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-4"
                data-testid="error-message"
              >
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-900 mb-1">Not Found</h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </motion.div>
            )}

            {/* Success Result */}
            {result && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-8 bg-green-50 border border-green-200 rounded-xl p-6"
                data-testid="certificate-result"
              >
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <h3 className="text-2xl font-bold text-green-900">Certificate Verified!</h3>
                </div>

                <div className="space-y-4">
                  <div className="border-b border-green-200 pb-3">
                    <p className="text-sm text-green-700 mb-1">Certificate ID</p>
                    <p className="text-lg font-bold text-green-900">{result.certificate_id}</p>
                  </div>

                  <div className="border-b border-green-200 pb-3">
                    <p className="text-sm text-green-700 mb-1">Student Name</p>
                    <p className="text-lg font-bold text-green-900">{result.student_name}</p>
                  </div>

                  {result.father_husband_name && (
                    <div className="border-b border-green-200 pb-3">
                      <p className="text-sm text-green-700 mb-1">Father/Husband Name</p>
                      <p className="text-lg font-bold text-green-900">{result.father_husband_name}</p>
                    </div>
                  )}

                  <div className="border-b border-green-200 pb-3">
                    <p className="text-sm text-green-700 mb-1">Course</p>
                    <p className="text-lg font-bold text-green-900">{result.course}</p>
                  </div>

                  {result.duration && (
                    <div className="border-b border-green-200 pb-3">
                      <p className="text-sm text-green-700 mb-1">Duration</p>
                      <p className="text-lg font-bold text-green-900">{result.duration}</p>
                    </div>
                  )}

                  {result.from_date && result.to_date && (
                    <div className="border-b border-green-200 pb-3">
                      <p className="text-sm text-green-700 mb-1">Course Period</p>
                      <p className="text-lg font-bold text-green-900">{result.from_date} to {result.to_date}</p>
                    </div>
                  )}

                  {result.issue_date && (
                    <div className="border-b border-green-200 pb-3">
                      <p className="text-sm text-green-700 mb-1">Issue Date</p>
                      <p className="text-lg font-bold text-green-900">{result.issue_date}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-green-700 mb-1">Grade</p>
                    <p className="text-lg font-bold text-green-900">{result.grade || 'Completed'}</p>
                  </div>
                </div>

                <div className="mt-6 bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-600">
                    This certificate is <strong className="text-green-600">authentic</strong> and issued by Genius Academy, Kalaburagi.
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-slate-600">
              If you have any questions about certificate verification,<br />
              please contact us at <a href="tel:9164998997" className="text-cyan-600 font-medium hover:text-cyan-700">9164998997</a>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CertificateVerifier;